/**
 * El repaso de mates, por cursos.
 *
 * Lo que distingue esto de cualquier app de cuentas que se baja gratis es que
 * **se acuerda de lo que cada niño falla**, y de lo que le cuesta aunque lo
 * acierte. Un acierto lento no es un acierto: es contar con los dedos, y es la
 * señal más útil que hay de qué toca practicar. Por eso cada intento guarda
 * cuánto tardó.
 *
 * La unidad de todo esto es la **destreza** —«restar con decimales», «la tabla
 * del siete», «dividir entre dos cifras»— y no el ejercicio suelto. Es lo que se
 * puede practicar, lo que se puede decirle a un adulto y lo único que generaliza:
 * de la tabla del siete hay ochenta y un ejercicios y de «sumar con decimales»
 * hay infinitos, pero las dos son una cosa que se sabe o no se sabe.
 *
 * Todo lo de aquí es una función pura. El azar entra como números entre cero y
 * uno que pone quien llama, y el tiempo entra medido desde fuera, porque los dos
 * acaban escritos en el registro: así una sesión se puede volver a reproducir
 * igual años después.
 */

import type { PracticeItem } from '../events.ts'

/** De tercero a sexto de primaria. */
export type Curso = 3 | 4 | 5 | 6

export interface Ejercicio {
  readonly destreza: string
  /** Lo que se le enseña: «34,5 + 12,7». */
  readonly pide: string
  /** La respuesta, ya en el formato que se espera: «47,2». */
  readonly solucion: string
}

/**
 * Lo que el niño elige repasar: las cuatro cuentas, o todo lo que hay.
 *
 * Existe porque lo pidió Gabriel: quería poder ponerse solo con las divisiones,
 * que es lo que no le sale. Las fracciones y los porcentajes van con la
 * división porque en el cuaderno son una división; lo demás —redondear,
 * potencias, negativos, combinadas— solo sale eligiendo «de todo».
 */
export type Familia = 'suma' | 'resta' | 'multiplicacion' | 'division'

export interface Destreza {
  readonly id: string
  readonly curso: Curso
  /** Cómo se le llama a esto delante de un adulto: «restar con decimales». */
  readonly titulo: string
  /** Sin familia, solo sale eligiendo «de todo». */
  readonly familia?: Familia
  readonly generar: (draw: () => number) => Ejercicio
}

/**
 * Un intento pasado. Solo hace falta saber de qué destreza era, si acertó y
 * cuánto tardó: lo que se preguntó exactamente no cambia en qué hay que insistir.
 */
export interface Intento {
  readonly destreza: string
  readonly ok: boolean
  readonly ms: number
  /** Lo hizo paso a paso, con la cuenta delante. Cuenta como practicado, no como sabido. */
  readonly ayuda?: boolean
}

/**
 * Lo que el registro guardó, leído como un intento.
 *
 * Las sesiones de los primeros días solo tenían tablas y se guardaban como dos
 * números y un signo. Se traducen a su destreza para que ese historial siga
 * contando: lo que un niño practicó en octubre no deja de haber pasado porque el
 * juego haya crecido.
 */
export function intentoDe(item: PracticeItem): Intento {
  if (item.destreza !== undefined) {
    return {
      destreza: item.destreza,
      ok: item.ok,
      ms: item.ms,
      ...(item.ayuda === true ? { ayuda: true } : {}),
    }
  }
  const vieja =
    item.op === ':' ? 'dividir-una-cifra' : `tabla-${Math.min(item.a ?? 2, item.b ?? 2)}`
  return { destreza: vieja, ok: item.ok, ms: item.ms }
}

/**
 * Cuántas preguntas tiene una sesión.
 *
 * Doce: suficientes para que el reparto por pesos se note y cortas para que un
 * niño de ocho años llegue al final sin cansarse. Vive aquí porque es lo que hace
 * de «terminar la sesión» algo con un significado fijo, y de eso depende el pago
 * de la misión.
 */
export const PREGUNTAS_POR_SESION = 12

// --- Números con decimales, sin decimales -------------------------------------

/**
 * Todo se calcula en milésimas enteras y se formatea al final.
 *
 * Con números con coma, `0,1 + 0,2` no da `0,3` sino `0,30000000000000004`, y un
 * niño que escribe `0,3` vería que ha fallado. En una app que corrige, eso no es
 * un detalle de precisión: es acusar de un error que no existe.
 */
const MIL = 1000

export function formatear(milesimas: number): string {
  const entero = Math.trunc(milesimas / MIL)
  const resto = Math.abs(milesimas % MIL)
  if (resto === 0) return String(entero)
  const decimales = String(resto).padStart(3, '0').replace(/0+$/, '')
  return `${entero},${decimales}`
}

/** Un entero entre `min` y `max`, los dos incluidos. */
function entre(draw: () => number, min: number, max: number): number {
  const dado = Math.min(Math.max(draw(), 0), 0.999_999)
  return min + Math.floor(dado * (max - min + 1))
}

/**
 * Una de las opciones. Se pide un valor de reserva en vez de afirmar que la lista
 * no está vacía: el compilador no tiene por qué creerse nada.
 */
function elegir<T>(draw: () => number, opciones: readonly T[], siNoHay: T): T {
  return opciones[entre(draw, 0, opciones.length - 1)] ?? siNoHay
}

const ej = (destreza: string, pide: string, solucion: string): Ejercicio => ({
  destreza,
  pide,
  solucion,
})

// --- El catálogo, por cursos --------------------------------------------------

/** Las tablas son nueve destrezas y no una: «la del siete» se sabe o no se sabe. */
const TABLAS: readonly Destreza[] = Array.from({ length: 9 }, (_, i) => i + 2).map((tabla) => ({
  id: `tabla-${tabla}`,
  curso: 3,
  titulo: `la tabla del ${tabla}`,
  familia: 'multiplicacion',
  generar: (draw) => {
    const otro = entre(draw, 2, 10)
    return ej(`tabla-${tabla}`, `${tabla} × ${otro}`, String(tabla * otro))
  },
}))

/**
 * Lo que se enseña en cada curso de primaria, siguiendo el currículo de la Junta
 * de Andalucía (Decreto 101/2023, saberes básicos del área de Matemáticas).
 *
 * El currículo va por **ciclos** —tercero con cuarto, quinto con sexto— y no por
 * cursos, así que el reparto curso a curso de aquí es una interpretación del
 * orden en que se suele dar dentro de cada ciclo. Por eso el curso se puede
 * cambiar a mano: quien sabe cómo va su clase es el maestro, no este fichero.
 *
 * Y no es el currículo entero: es lo que se puede preguntar con un teclado
 * numérico y corregir sin ambigüedad. Queda fuera, a propósito, todo lo que se
 * responde con un dibujo, una explicación o un sí y un no — geometría, medida,
 * estadística, resolución de problemas con enunciado. Eso no es que sea menos
 * importante; es que un juego de teclas no es la herramienta.
 */
export const CATALOGO: readonly Destreza[] = [
  // --- Tercero: las cuatro operaciones con naturales, y asomarse a la coma.
  {
    id: 'suma-llevadas',
    curso: 3,
    titulo: 'sumar llevando',
    familia: 'suma',
    generar: (draw) => {
      // Se fuerza la llevada: sin ella no se practica lo que se quiere practicar.
      const a = entre(draw, 106, 899)
      const b = entre(draw, 105, 999 - (a % 100))
      const conLlevada = (a % 10) + (b % 10) >= 10 ? b : b + (10 - ((a % 10) + (b % 10)))
      return ej('suma-llevadas', `${a} + ${conLlevada}`, String(a + conLlevada))
    },
  },
  {
    id: 'resta-llevadas',
    curso: 3,
    titulo: 'restar llevando',
    familia: 'resta',
    generar: (draw) => {
      const a = entre(draw, 200, 989)
      const b = entre(draw, 101, a - 10)
      // Que la unidad de abajo sea mayor obliga a la llevada.
      const conLlevada = b % 10 > a % 10 ? b : b - (b % 10) + ((a % 10) + 1)
      const menor = Math.min(conLlevada, a - 1)
      return ej('resta-llevadas', `${a} - ${menor}`, String(a - menor))
    },
  },
  ...TABLAS,
  {
    id: 'multiplicar-una-cifra',
    curso: 3,
    titulo: 'multiplicar por una cifra',
    familia: 'multiplicacion',
    generar: (draw) => {
      const a = entre(draw, 23, 499)
      const b = entre(draw, 3, 9)
      return ej('multiplicar-una-cifra', `${a} × ${b}`, String(a * b))
    },
  },
  {
    id: 'dividir-una-cifra',
    curso: 3,
    titulo: 'dividir entre una cifra',
    familia: 'division',
    generar: (draw) => {
      // Se construye desde la multiplicación: así la división es exacta siempre.
      // Un resto aquí no sería dificultad, sería escribir lo correcto y que la
      // app diga que no.
      const divisor = entre(draw, 2, 9)
      const cociente = entre(draw, 11, 99)
      return ej('dividir-una-cifra', `${divisor * cociente} : ${divisor}`, String(cociente))
    },
  },
  {
    id: 'fraccion-sencilla',
    curso: 3,
    titulo: 'mitades, tercios y cuartos',
    familia: 'division',
    generar: (draw) => {
      const partes = elegir(draw, [2, 3, 4], 2)
      const nombres: Record<number, string> = { 2: 'la mitad', 3: 'un tercio', 4: 'un cuarto' }
      const cociente = entre(draw, 4, 40)
      return ej(
        'fraccion-sencilla',
        `${nombres[partes] ?? 'la mitad'} de ${partes * cociente}`,
        String(cociente),
      )
    },
  },
  {
    id: 'decimas',
    curso: 3,
    titulo: 'sumar décimas',
    familia: 'suma',
    generar: (draw) => {
      const a = entre(draw, 11, 89) * 100
      const b = entre(draw, 11, 89) * 100
      return ej('decimas', `${formatear(a)} + ${formatear(b)}`, formatear(a + b))
    },
  },

  // --- Cuarto: la coma en serio, y multiplicar por dos cifras.
  {
    id: 'suma-decimales',
    curso: 4,
    titulo: 'sumar con decimales',
    familia: 'suma',
    generar: (draw) => {
      const a = entre(draw, 105, 9990) * 10
      const b = entre(draw, 105, 9990) * 10
      return ej('suma-decimales', `${formatear(a)} + ${formatear(b)}`, formatear(a + b))
    },
  },
  {
    id: 'resta-decimales',
    curso: 4,
    titulo: 'restar con decimales',
    familia: 'resta',
    generar: (draw) => {
      const a = entre(draw, 2005, 9990) * 10
      const b = entre(draw, 105, 1990) * 10
      return ej('resta-decimales', `${formatear(a)} - ${formatear(b)}`, formatear(a - b))
    },
  },
  {
    id: 'multiplicar-dos-cifras',
    curso: 4,
    titulo: 'multiplicar por dos cifras',
    familia: 'multiplicacion',
    generar: (draw) => {
      const a = entre(draw, 23, 99)
      const b = entre(draw, 12, 49)
      return ej('multiplicar-dos-cifras', `${a} × ${b}`, String(a * b))
    },
  },
  {
    id: 'fraccion-cantidad',
    curso: 4,
    titulo: 'fracción de una cantidad',
    familia: 'division',
    generar: (draw) => {
      const denominador = elegir(draw, [2, 3, 4, 5], 2)
      const numerador = entre(draw, 1, denominador - 1)
      const unidad = entre(draw, 3, 25)
      return ej(
        'fraccion-cantidad',
        `${numerador}/${denominador} de ${denominador * unidad}`,
        String(numerador * unidad),
      )
    },
  },
  {
    id: 'suma-cuatro-cifras',
    curso: 4,
    titulo: 'sumar números largos',
    familia: 'suma',
    generar: (draw) => {
      const a = entre(draw, 1234, 8999)
      const b = entre(draw, 1234, 8999)
      return ej('suma-cuatro-cifras', `${a} + ${b}`, String(a + b))
    },
  },

  // --- Quinto: decimales por todas partes y dividir entre dos cifras.
  {
    id: 'multiplicar-decimal-natural',
    curso: 5,
    titulo: 'multiplicar un decimal',
    familia: 'multiplicacion',
    generar: (draw) => {
      const a = entre(draw, 12, 98) * 100
      const b = entre(draw, 3, 9)
      return ej('multiplicar-decimal-natural', `${formatear(a)} × ${b}`, formatear(a * b))
    },
  },
  {
    id: 'dividir-dos-cifras',
    curso: 4,
    titulo: 'dividir entre dos cifras',
    familia: 'division',
    generar: (draw) => {
      const divisor = entre(draw, 12, 45)
      const cociente = entre(draw, 12, 60)
      return ej('dividir-dos-cifras', `${divisor * cociente} : ${divisor}`, String(cociente))
    },
  },
  {
    id: 'dividir-decimal',
    curso: 5,
    titulo: 'dividir un decimal',
    familia: 'division',
    generar: (draw) => {
      const divisor = entre(draw, 2, 8)
      const cociente = entre(draw, 12, 99) * 10
      return ej(
        'dividir-decimal',
        `${formatear(cociente * divisor)} : ${divisor}`,
        formatear(cociente),
      )
    },
  },
  {
    id: 'porcentaje-sencillo',
    curso: 5,
    titulo: 'porcentajes fáciles',
    familia: 'division',
    generar: (draw) => {
      const tanto = elegir(draw, [10, 25, 50], 10)
      const base = entre(draw, 2, 40) * 20
      return ej('porcentaje-sencillo', `${tanto}% de ${base}`, String((base * tanto) / 100))
    },
  },

  {
    id: 'redondeo',
    curso: 5,
    titulo: 'redondear',
    generar: (draw) => {
      const milesimas = entre(draw, 1005, 9995)
      const decimas = Math.round(milesimas / 100) * 100
      return ej('redondeo', `redondea ${formatear(milesimas)} a la décima`, formatear(decimas))
    },
  },
  {
    id: 'multiplos',
    curso: 5,
    titulo: 'múltiplos',
    familia: 'multiplicacion',
    generar: (draw) => {
      const de = entre(draw, 3, 12)
      const cuantas = entre(draw, 4, 12)
      return ej('multiplos', `el múltiplo de ${de} que hace ${cuantas}`, String(de * cuantas))
    },
  },

  // --- Sexto: lo de antes, más fino, y el orden de las operaciones.
  {
    id: 'multiplicar-decimal-decimal',
    curso: 6,
    titulo: 'multiplicar dos decimales',
    familia: 'multiplicacion',
    generar: (draw) => {
      const a = entre(draw, 12, 95) * 100
      const b = entre(draw, 12, 40) * 100
      return ej(
        'multiplicar-decimal-decimal',
        `${formatear(a)} × ${formatear(b)}`,
        formatear((a * b) / MIL),
      )
    },
  },
  {
    id: 'dividir-entre-decimal',
    curso: 6,
    titulo: 'dividir entre un decimal',
    familia: 'division',
    generar: (draw) => {
      const divisor = elegir(draw, [200, 250, 500, 1500, 2500], 500)
      const cociente = entre(draw, 3, 40)
      return ej(
        'dividir-entre-decimal',
        `${formatear(divisor * cociente)} : ${formatear(divisor)}`,
        String(cociente),
      )
    },
  },
  {
    id: 'potencias',
    curso: 6,
    titulo: 'potencias',
    generar: (draw) => {
      const cuadrado = draw() < 0.7
      const base = cuadrado ? entre(draw, 2, 15) : entre(draw, 2, 6)
      return ej(
        'potencias',
        `${base}${cuadrado ? '²' : '³'}`,
        String(cuadrado ? base * base : base * base * base),
      )
    },
  },
  {
    id: 'porcentaje',
    curso: 6,
    titulo: 'porcentajes',
    familia: 'division',
    generar: (draw) => {
      const tanto = elegir(draw, [5, 15, 20, 30, 75], 10)
      const base = entre(draw, 2, 30) * 20
      return ej('porcentaje', `${tanto}% de ${base}`, String((base * tanto) / 100))
    },
  },
  {
    id: 'enteros',
    curso: 6,
    titulo: 'números negativos',
    generar: (draw) => {
      const bajo = entre(draw, 2, 12)
      const sube = entre(draw, 3, 20)
      // Enunciado de termómetro: es como se presentan los negativos en primaria,
      // y evita que el resultado parezca una cuenta sin sentido.
      return ej('enteros', `-${bajo} + ${sube}`, String(sube - bajo))
    },
  },
  {
    id: 'fraccion-decimal',
    curso: 6,
    titulo: 'fracciones en decimal',
    familia: 'division',
    generar: (draw) => {
      // Fracción y valor en un solo texto: dos listas en paralelo se
      // desincronizan el día que alguien añade una y olvida la otra.
      const cual = elegir(
        draw,
        ['1/2=0,5', '1/4=0,25', '3/4=0,75', '1/5=0,2', '3/5=0,6', '1/10=0,1', '7/10=0,7'],
        '1/2=0,5',
      )
      const [fraccion = '1/2', valor = '0,5'] = cual.split('=')
      return ej('fraccion-decimal', `${fraccion} en decimal`, valor)
    },
  },
  {
    id: 'combinadas',
    curso: 6,
    titulo: 'operaciones combinadas',
    generar: (draw) => {
      // Sale una con paréntesis y otra sin ellos a propósito: lo que se practica
      // es justo que no es lo mismo.
      const a = entre(draw, 3, 20)
      const b = entre(draw, 2, 9)
      const c = entre(draw, 2, 9)
      return draw() < 0.5
        ? ej('combinadas', `${a} + ${b} × ${c}`, String(a + b * c))
        : ej('combinadas', `(${a} + ${b}) × ${c}`, String((a + b) * c))
    },
  },
]

/**
 * En qué curso está, por la edad, cuando nadie lo ha dicho.
 *
 * En España se empieza primero con seis años cumplidos en el año, así que tercero
 * son ocho. Es una estimación y por eso se puede cambiar a mano: hay niños
 * repetidores, adelantados, y niños de diciembre.
 */
export function cursoPorEdad(edad: number | undefined): Curso {
  if (edad === undefined) return 4
  if (edad <= 8) return 3
  if (edad === 9) return 4
  if (edad === 10) return 5
  return 6
}

/**
 * Las destrezas que entran para un curso: las suyas y las de antes.
 *
 * Un niño de sexto sigue practicando las tablas — de hecho es donde más se nota
 * no tenerlas. Dejar fuera lo de cursos anteriores convertiría el juego en un
 * examen del temario de este mes.
 */
export function destrezasDe(curso: Curso, familia?: Familia): readonly Destreza[] {
  return CATALOGO.filter(
    (d) => d.curso <= curso && (familia === undefined || d.familia === familia),
  )
}

/** Las familias que tienen algo que practicar en un curso, en el orden del cuaderno. */
export function familiasDe(curso: Curso): readonly Familia[] {
  const todas: readonly Familia[] = ['suma', 'resta', 'multiplicacion', 'division']
  return todas.filter((f) => destrezasDe(curso, f).length > 0)
}

// --- Qué practicar ------------------------------------------------------------

/** Sin datos suficientes, «lento» son seis segundos. */
const LENTO_POR_DEFECTO = 6000
const LENTO_MINIMO = 2500
const POCOS_DATOS = 8

/**
 * A partir de cuándo una respuesta correcta cuenta como lenta.
 *
 * No es un número fijo: lo que para un niño de once es lento, para uno de ocho es
 * normal. Se calcula con su propia mediana y se dobla, así que el umbral lo pone
 * cada niño con sus respuestas. Con menos de ocho datos no hay mediana que valga
 * y se usa un valor de partida.
 */
export function umbralLento(historial: readonly Intento[]): number {
  const buenos = historial
    .filter((i) => i.ok)
    .map((i) => i.ms)
    .sort((x, y) => x - y)
  if (buenos.length < POCOS_DATOS) return LENTO_POR_DEFECTO
  const mediana = buenos[Math.floor(buenos.length / 2)] ?? LENTO_POR_DEFECTO
  return Math.max(LENTO_MINIMO, mediana * 2)
}

const PESO_SIN_VER = 3
const PESO_FALLADO = 8
const PESO_LENTO = 3
const PESO_BIEN = 1
const PESO_SOLTURA = 0.4
/** Lo del curso de este año sale más que lo de cursos pasados. */
const EMPUJE_DEL_CURSO = 1.6

/**
 * Cuánto insistir en una destreza. Cuanto más alto, más probable que salga.
 *
 * Mira los dos últimos intentos y nada más: lo que importa es cómo va ahora, no
 * un promedio de todo el curso. Y ningún peso llega a cero — una destreza que se
 * deja de preguntar del todo se olvida, que es justo lo que se quiere evitar.
 */
export function peso(
  destreza: Destreza,
  historial: readonly Intento[],
  curso: Curso,
  umbral = umbralLento(historial),
): number {
  const suyos = historial.filter((i) => i.destreza === destreza.id)
  const ultimo = suyos.at(-1)
  const empuje = destreza.curso === curso ? EMPUJE_DEL_CURSO : 1

  if (!ultimo) return PESO_SIN_VER * empuje
  if (!ultimo.ok) return PESO_FALLADO * empuje
  // Hecho con los pasos delante vale lo que un acierto lento: se practicó,
  // pero todavía no se sabe. Tiene que volver a salir.
  if (ultimo.ms > umbral || ultimo.ayuda === true) return PESO_LENTO * empuje
  const penultimo = suyos.at(-2)
  if (penultimo?.ok === true && penultimo.ms <= umbral) return PESO_SOLTURA * empuje
  return PESO_BIEN * empuje
}

/**
 * El siguiente ejercicio.
 *
 * `draw` da números entre cero y uno, los pone quien llama y se guardan en el
 * registro. `evitar` es la destreza que acaba de salir: encadenar dos de la misma
 * se siente como una burla aunque el reparto sea correcto.
 */
export function siguienteEjercicio(
  destrezas: readonly Destreza[],
  historial: readonly Intento[],
  curso: Curso,
  draw: () => number,
  evitar?: string,
): Ejercicio | undefined {
  if (destrezas.length === 0) return undefined
  const posibles = destrezas.filter((d) => d.id !== evitar)
  const lista = posibles.length > 0 ? posibles : destrezas

  const umbral = umbralLento(historial)
  const pesos = lista.map((d) => peso(d, historial, curso, umbral))
  const total = pesos.reduce((suma, x) => suma + x, 0)
  if (total <= 0) return lista[0]?.generar(draw)

  let queda = Math.min(Math.max(draw(), 0), 0.999_999) * total
  for (const [i, destreza] of lista.entries()) {
    queda -= pesos[i] ?? 0
    if (queda < 0) return destreza.generar(draw)
  }
  return lista.at(-1)?.generar(draw)
}

/**
 * Si lo que ha escrito es la respuesta.
 *
 * Se compara en texto y no en número: `0,1 + 0,2` con números con coma no da
 * `0,3`, y aquí eso significaría decirle a un niño que se ha equivocado cuando no.
 * Y se acepta el punto además de la coma, y los ceros de más por la derecha:
 * `47,20` es lo mismo que `47,2` y quien lo escribe lo sabe.
 */
export function normalizar(texto: string): string {
  const limpio = texto.trim().replace(/\s+/g, '').replace('.', ',')
  if (!limpio.includes(',')) return limpio.replace(/^0+(?=\d)/, '')
  const [entera = '', decimal = ''] = limpio.split(',')
  const sinCeros = decimal.replace(/0+$/, '')
  const entero = entera.replace(/^0+(?=\d)/, '') || '0'
  return sinCeros === '' ? entero : `${entero},${sinCeros}`
}

export function acierta(ejercicio: Ejercicio, dicho: string): boolean {
  return normalizar(dicho) === normalizar(ejercicio.solucion) && dicho.trim().length > 0
}

// --- Lo que se le cuenta a un adulto -----------------------------------------

export interface Balance {
  readonly aciertos: number
  readonly fallos: number
  /** Aciertos que tardaron más de lo suyo: lo que toca practicar. */
  readonly lentos: number
  /** Hechas paso a paso. Van dentro de los aciertos, y se dicen aparte. */
  readonly conAyuda: number
}

export function balance(items: readonly Intento[], historial: readonly Intento[] = items): Balance {
  const umbral = umbralLento(historial)
  return {
    aciertos: items.filter((i) => i.ok).length,
    fallos: items.filter((i) => !i.ok).length,
    lentos: items.filter((i) => i.ok && i.ms > umbral && i.ayuda !== true).length,
    conAyuda: items.filter((i) => i.ok && i.ayuda === true).length,
  }
}

export interface Flojo {
  readonly destreza: string
  readonly titulo: string
  readonly fallos: number
  readonly lentos: number
}

const TITULOS: ReadonlyMap<string, string> = new Map(CATALOGO.map((d) => [d.id, d.titulo]))

/**
 * Lo que peor le va, para que un adulto lo lea de un vistazo.
 *
 * Por destreza, que es lo que se puede practicar: «restar con decimales» es algo
 * que hacer el sábado por la mañana, y «34,5 − 12,7» no es nada. Cuenta los
 * lentos junto a los fallos a propósito: es la mitad de la información.
 */
export function loQueCuesta(historial: readonly Intento[], cuantos = 3): readonly Flojo[] {
  const umbral = umbralLento(historial)
  const por = new Map<string, { fallos: number; lentos: number }>()

  for (const intento of historial) {
    const cuenta = por.get(intento.destreza) ?? { fallos: 0, lentos: 0 }
    if (!intento.ok) cuenta.fallos += 1
    else if (intento.ms > umbral) cuenta.lentos += 1
    por.set(intento.destreza, cuenta)
  }

  return [...por.entries()]
    .map(([destreza, cuenta]) => ({
      destreza,
      titulo: TITULOS.get(destreza) ?? destreza,
      fallos: cuenta.fallos,
      lentos: cuenta.lentos,
    }))
    .filter((f) => f.fallos > 0 || f.lentos > 0)
    .sort((a, b) => b.fallos * 2 + b.lentos - (a.fallos * 2 + a.lentos))
    .slice(0, cuantos)
}
